// Phase SIKA-5e/2 — Claude.ai-style inline attachment card.
//
// Used to render structured card_type messages (PlanCard, SlipCard,
// ScenarioCard, etc.) inline in the assistant's prose flow. Provides
// the flat hairline-bordered chrome that mirrors the Claude.ai
// "Strikza dashboard autotrading v1.0" attachment block.
//
// This component is presentational — the actual card BODY (the
// rendered content) is passed as children. Existing SikaCardRenderer
// keeps owning the per-card_type rendering; this wraps it.
//
// CSS lives in src/styles/super-acca.css under .sa-root .sika-chat-2
// .sc2-inline-card*. The host page must mount .sika-chat-2 inside
// .sa-root for the styles to apply.

import type { ReactNode } from "react";

export interface InlineCardProps {
  /** Title text shown in the card header (e.g. card_type or human title). */
  title: string;
  /** Meta line shown under the title (e.g. "PlanCard · GH₵200k goal"). */
  meta?: string;
  /** Icon glyph or short string shown in the leading badge (e.g. "P" for Plan). */
  iconGlyph?: string;
  /** Optional CTA on the right (e.g. "VIEW", "OPEN"). */
  cta?: {
    label: string;
    onClick: () => void;
  } | null;
  /** Full card body — typically a <SikaCardRenderer />. */
  children?: ReactNode;
  /** Forwarded data-testid for vitest. */
  testId?: string;
}

/**
 * Compact attachment-style card with optional rich body.
 *
 * When `children` is provided, the card renders as a stacked block:
 * the header row (icon + title + meta + cta) sits on top of the body.
 * When omitted, it renders as a single header row only — useful for
 * pure-link references like document attachments.
 */
export function InlineCard({
  title,
  meta,
  iconGlyph,
  cta = null,
  children,
  testId,
}: InlineCardProps): JSX.Element {
  const hasBody = children != null;

  return (
    <div
      className="sc2-inline-card"
      data-testid={testId ?? "sc2-inline-card"}
      style={hasBody ? { flexDirection: "column", alignItems: "stretch" } : undefined}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
        }}
      >
        <div className="sc2-inline-card-icon" aria-hidden>
          {iconGlyph ?? "·"}
        </div>
        <div className="sc2-inline-card-body">
          <div className="sc2-inline-card-title">{title}</div>
          {meta ? <div className="sc2-inline-card-meta">{meta}</div> : null}
        </div>
        {cta ? (
          <button
            type="button"
            className="sc2-inline-card-cta"
            onClick={cta.onClick}
            data-testid="sc2-inline-card-cta"
          >
            {cta.label}
          </button>
        ) : null}
      </div>
      {hasBody ? (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--sa-hairline)",
            width: "100%",
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Map a card_type code (PlanCard, SlipCard, etc.) to a short icon
 * glyph + human title. Falls back to first-letter + raw card_type.
 */
export function describeCardType(cardType: string): {
  iconGlyph: string;
  title: string;
} {
  const map: Record<string, { iconGlyph: string; title: string }> = {
    PlanCard: { iconGlyph: "P", title: "Plan" },
    SlipCard: { iconGlyph: "S", title: "Slip" },
    SlipDetailCard: { iconGlyph: "S", title: "Slip detail" },
    ScenarioCard: { iconGlyph: "?", title: "Scenario" },
    ProjectionCard: { iconGlyph: "Π", title: "Projection" },
    BankrollCard: { iconGlyph: "₵", title: "Bankroll" },
    GoalCard: { iconGlyph: "G", title: "Goal" },
    ChainCard: { iconGlyph: "C", title: "Chain" },
    ReviewCard: { iconGlyph: "R", title: "Strategy review" },
    CertificateCard: { iconGlyph: "✓", title: "Verified return" },
    OpeningCard: { iconGlyph: "·", title: "Status" },
  };
  return (
    map[cardType] ?? {
      iconGlyph: cardType.charAt(0).toUpperCase() || "·",
      title: cardType,
    }
  );
}
